/**
 * @module MasterSettingsPage
 *
 * 基本情報マスタページコンポーネント
 * - 通知設定セクション（通知期限日数、バッチ実行時刻）
 * - メール設定セクション（送信元、件名テンプレート、本文テンプレート）
 * - SMTP設定セクション（ホスト、ポート、認証、TLS、テスト送信ボタン）
 * - 変更履歴テーブル
 * - 保存成功時のSnackbar表示
 *
 * Requirements: 15.1, 15.2, 15.3, 15.4, 15.5, 15.6
 */
import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useSnackbar } from '../components/AppSnackbar';
import { useAuth } from '../hooks/useAuth';

// ============================================================
// 型定義
// ============================================================

interface MasterSetting {
  id: string;
  key: string;
  value: string;
  description: string | null;
}

interface SettingHistoryItem {
  id: string;
  key: string;
  oldValue: string | null;
  newValue: string;
  changedBy: string;
  changedAt: string;
}

interface SettingsFormData {
  notification_days_default: string;
  batch_execution_time: string;
  mail_from_address: string;
  mail_subject_template: string;
  mail_body_template: string;
  smtp_host: string;
  smtp_port: string;
  smtp_user: string;
  smtp_password: string;
  smtp_tls_enabled: string;
  lark_app_id: string;
  lark_app_secret: string;
  lark_notification_enabled: string;
}

// ============================================================
// 定数
// ============================================================

const INITIAL_FORM_DATA: SettingsFormData = {
  notification_days_default: '30',
  batch_execution_time: '09:00',
  mail_from_address: '',
  mail_subject_template: '',
  mail_body_template: '',
  smtp_host: '',
  smtp_port: '587',
  smtp_user: '',
  smtp_password: '',
  smtp_tls_enabled: 'true',
  lark_app_id: '',
  lark_app_secret: '',
  lark_notification_enabled: 'false',
};

const KEY_LABELS: Record<string, string> = {
  notification_days_default: '通知期限日数（デフォルト）',
  batch_execution_time: 'バッチ実行時刻',
  mail_from_address: '送信元メールアドレス',
  mail_subject_template: 'メール件名テンプレート',
  mail_body_template: 'メール本文テンプレート',
  smtp_host: 'SMTPホスト',
  smtp_port: 'SMTPポート',
  smtp_user: 'SMTP認証ユーザー',
  smtp_password: 'SMTP認証パスワード',
  smtp_tls_enabled: 'TLS有効化',
  lark_app_id: 'Lark App ID',
  lark_app_secret: 'Lark App Secret',
  lark_notification_enabled: 'Lark通知有効化',
};

// ============================================================
// ヘルパー関数
// ============================================================

function settingsToFormData(settings: MasterSetting[]): SettingsFormData {
  const formData = { ...INITIAL_FORM_DATA };
  for (const setting of settings) {
    if (setting.key in formData) {
      (formData as Record<string, string>)[setting.key] = setting.value;
    }
  }
  return formData;
}

function formDataToPayload(formData: SettingsFormData) {
  return {
    settings: Object.entries(formData).map(([key, value]) => ({ key, value })),
  };
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================
// メインコンポーネント
// ============================================================

const MasterSettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError, showInfo } = useSnackbar();
  const { user } = useAuth();
  const [formData, setFormData] = useState<SettingsFormData>(INITIAL_FORM_DATA);

  // 設定一覧取得
  const {
    data: settingsData,
    isLoading: isLoadingSettings,
    error: settingsError,
  } = useQuery<{ data: MasterSetting[] }>({
    queryKey: ['master-settings'],
    queryFn: async () => {
      const res = await api.get('/master-settings');
      return res.data;
    },
  });

  // 変更履歴取得
  const {
    data: historyData,
    isLoading: isLoadingHistory,
  } = useQuery<{ data: SettingHistoryItem[] }>({
    queryKey: ['master-settings-history'],
    queryFn: async () => {
      const res = await api.get('/master-settings/history');
      return res.data;
    },
  });

  // フォームデータの初期化
  useEffect(() => {
    if (settingsData?.data) {
      setFormData(settingsToFormData(settingsData.data));
    }
  }, [settingsData]);

  // 設定保存
  const saveMutation = useMutation({
    mutationFn: async (payload: ReturnType<typeof formDataToPayload>) => {
      const res = await api.put('/master-settings', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['master-settings'] });
      queryClient.invalidateQueries({ queryKey: ['master-settings-history'] });
      showSuccess('設定を保存しました');
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      showError(axiosErr.response?.data?.message ?? '設定の保存に失敗しました');
    },
  });

  // テストメール送信
  const [testMailTo, setTestMailTo] = useState(user?.email ?? '');
  const testMailMutation = useMutation({
    mutationFn: async (to: string) => {
      const res = await api.post('/master-settings/test-mail', { to });
      return res.data;
    },
    onSuccess: () => {
      showInfo('テストメールを送信しました');
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      showError(axiosErr.response?.data?.message ?? 'テストメールの送信に失敗しました');
    },
  });

  // テストLark DM送信
  const [testLarkTo, setTestLarkTo] = useState(user?.email ?? '');
  const testLarkMutation = useMutation({
    mutationFn: async (to: string) => {
      const res = await api.post('/master-settings/test-lark', { to });
      return res.data;
    },
    onSuccess: () => {
      showInfo('Larkテストメッセージを送信しました');
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      showError(axiosErr.response?.data?.message ?? 'Larkテスト送信に失敗しました');
    },
  });

  // ハンドラー
  const handleChange = (key: keyof SettingsFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleCheckboxChange = (key: keyof SettingsFormData) => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.checked ? 'true' : 'false' }));
  };

  const handleSave = () => {
    saveMutation.mutate(formDataToPayload(formData));
  };

  const handleTestMail = () => {
    if (!testMailTo.trim()) {
      showError('送信先メールアドレスを入力してください');
      return;
    }
    testMailMutation.mutate(testMailTo.trim());
  };

  const handleTestLark = () => {
    if (!testLarkTo.trim()) {
      showError('送信先メールアドレスを入力してください');
      return;
    }
    testLarkMutation.mutate(testLarkTo.trim());
  };

  // ローディング状態
  if (isLoadingSettings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // エラー状態
  if (settingsError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        設定データの取得に失敗しました。
      </Alert>
    );
  }

  const history = historyData?.data ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px - 48px)', overflow: 'hidden' }}>
      {/* Fixed header portion */}
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            基本情報マスタ
          </Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? '保存中...' : '設定を保存'}
          </Button>
        </Box>
      </Box>

      {/* Scrollable content portion */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
        {/* 通知設定セクション */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            通知設定
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <TextField
              label="通知期限日数（デフォルト）"
              type="number"
              value={formData.notification_days_default}
              onChange={handleChange('notification_days_default')}
              inputProps={{ min: 0 }}
              sx={{ width: 250 }}
            />
            <TextField
              label="バッチ実行時刻"
              type="time"
              value={formData.batch_execution_time}
              onChange={handleChange('batch_execution_time')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            ※ 設定変更は次回バッチ実行時から適用されます。
          </Typography>
        </Paper>

        {/* メール設定セクション */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            メール設定
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="送信元メールアドレス"
              type="email"
              value={formData.mail_from_address}
              onChange={handleChange('mail_from_address')}
              fullWidth
            />
            <TextField
              label="メール件名テンプレート"
              value={formData.mail_subject_template}
              onChange={handleChange('mail_subject_template')}
              fullWidth
            />
            <TextField
              label="メール本文テンプレート"
              value={formData.mail_body_template}
              onChange={handleChange('mail_body_template')}
              multiline
              rows={6}
              fullWidth
            />
            <Typography variant="body2" color="text.secondary">
              利用可能なプレースホルダー変数: {'{{asset_number}}'}, {'{{category}}'}, {'{{deadline_type}}'}, {'{{deadline_date}}'}, {'{{days_remaining}}'}, {'{{manager_name}}'}
            </Typography>
          </Box>
        </Paper>

        {/* SMTP設定セクション */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            SMTP設定
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {/* ブラウザのパスワード自動保存ダイアログを抑制するためのダミーフィールド */}
          <input type="text" name="prevent_autofill" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
          <input type="password" name="prevent_autofill_pass" style={{ display: 'none' }} tabIndex={-1} autoComplete="new-password" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <TextField
                label="SMTPホスト"
                value={formData.smtp_host}
                onChange={handleChange('smtp_host')}
                sx={{ flex: 1, minWidth: 250 }}
              />
              <TextField
                label="SMTPポート"
                type="number"
                value={formData.smtp_port}
                onChange={handleChange('smtp_port')}
                inputProps={{ min: 1, max: 65535 }}
                sx={{ width: 150 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <TextField
                label="認証ユーザー"
                value={formData.smtp_user}
                onChange={handleChange('smtp_user')}
                sx={{ flex: 1, minWidth: 250 }}
              />
              <TextField
                label="認証パスワード"
                type="password"
                value={formData.smtp_password}
                onChange={handleChange('smtp_password')}
                autoComplete="new-password"
                inputProps={{ autoComplete: 'new-password' }}
                sx={{ flex: 1, minWidth: 250 }}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.smtp_tls_enabled === 'true'}
                  onChange={handleCheckboxChange('smtp_tls_enabled')}
                />
              }
              label="TLS有効化"
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="テスト送信先メールアドレス"
                type="email"
                value={testMailTo}
                onChange={(e) => setTestMailTo(e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={handleTestMail}
                disabled={testMailMutation.isPending || !testMailTo.trim()}
              >
                {testMailMutation.isPending ? '送信中...' : 'テスト送信'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Lark設定セクション */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Lark設定
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.lark_notification_enabled === 'true'}
                  onChange={handleCheckboxChange('lark_notification_enabled')}
                />
              }
              label="Lark通知有効化"
            />
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <TextField
                label="App ID"
                value={formData.lark_app_id}
                onChange={handleChange('lark_app_id')}
                sx={{ flex: 1, minWidth: 250 }}
              />
              <TextField
                label="App Secret"
                type="password"
                value={formData.lark_app_secret}
                onChange={handleChange('lark_app_secret')}
                autoComplete="new-password"
                inputProps={{ autoComplete: 'new-password' }}
                sx={{ flex: 1, minWidth: 250 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="テスト送信先メールアドレス"
                type="email"
                value={testLarkTo}
                onChange={(e) => setTestLarkTo(e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={handleTestLark}
                disabled={testLarkMutation.isPending || !testLarkTo.trim()}
              >
                {testLarkMutation.isPending ? '送信中...' : 'Larkテスト送信'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              ※ Lark Open Platform のアプリ設定で、メールアドレスによるユーザー検索権限を付与してください。
            </Typography>
          </Box>
        </Paper>

        {/* 変更履歴セクション */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            変更履歴
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {isLoadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              変更履歴はありません。
            </Typography>
          ) : (
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>変更日時</TableCell>
                    <TableCell>変更者</TableCell>
                    <TableCell>設定項目</TableCell>
                    <TableCell>変更前</TableCell>
                    <TableCell>変更後</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{formatDateTime(item.changedAt)}</TableCell>
                      <TableCell>{item.changedBy}</TableCell>
                      <TableCell>{KEY_LABELS[item.key] ?? item.key}</TableCell>
                      <TableCell>{item.oldValue ?? '-'}</TableCell>
                      <TableCell>{item.newValue}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default MasterSettingsPage;
